// Documentos pessoais: arquivos de texto (.txt, .md, .csv) e PDF com texto nativo guardados em trechos, só da conta (/api/client/documents*).
// O envio lê o arquivo no navegador (File.text) e manda o texto; o servidor valida, divide em trechos e indexa.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Check, Eye, FileText, Image as ImageIcon, Share2, Trash2, Upload, X } from 'lucide-react';
import { api, ApiError, when, type Doc, type OrgDirectory } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import { DocViewer, type ViewerTarget } from '../components/DocViewer';
import '../styles/documents.css';
import '../styles/company.css';   // caixas de marcação do diálogo de compartilhar

const MAX_BYTES = 2 * 1024 * 1024;   // mesmo teto do servidor (2 MB de texto): acima disso o arquivo nem é enviado
const TOO_BIG = 'documento grande demais (máximo 2 MB de texto)';
const MAX_PDF_BYTES = 8 * 1024 * 1024;   // PDF: o texto extraído continua limitado a 2 MB no servidor
const PDF_TOO_BIG = 'PDF grande demais (máximo 8 MB)';
const IMG_TOO_BIG = 'imagem grande demais (máximo 8 MB)';
const ACCEPT = '.txt,.md,.csv,.pdf,.png,.jpg,.jpeg,.webp,text/plain,text/markdown,text/csv,application/pdf,image/png,image/jpeg,image/webp';
const isImageName = (n: string) => /\.(png|jpe?g|webp)$/i.test(n);

/** Base64 do arquivo sem o prefixo `data:…;base64,` (FileReader evita estourar a pilha com arquivos grandes). */
const toBase64 = (f: File) => new Promise<string>((ok, no) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).split(',')[1] ?? '');
  r.onerror = () => no(new Error('não foi possível ler o arquivo'));
  r.readAsDataURL(f);
});
const TYPE_LABEL: Record<string, string> = { 'text/plain': 'TXT', 'text/markdown': 'MD', 'text/csv': 'CSV', 'application/pdf': 'PDF', 'image/png': 'PNG', 'image/jpeg': 'JPG', 'image/webp': 'WEBP' };

const fmtSize = (b: number) => {
  const n = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return b < 1024 ? `${b} B` : b < 1048576 ? `${n(b / 1024)} KB` : `${n(b / 1048576)} MB`;
};

/** Compartilhar um documento próprio (conta de empresa): pessoas e setores da MESMA empresa, via GET/PUT /api/client/documents/:id/shares. */
function ShareDialog({ doc, onClose, onSaved }: { doc: Doc; onClose: () => void; onSaved: (n: { users: number; sectors: number }) => void }) {
  const [dir, setDir] = useState<OrgDirectory | null>(null);
  const [users, setUsers] = useState<string[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const flip = (l: string[], id: string) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]);
  const load = useCallback(() => {
    setError(null); setDir(null);
    Promise.all([api<OrgDirectory>('/api/client/org/directory'), api<{ users: { id: string }[]; sectors: { id: string }[] }>(`/api/client/documents/${doc.id}/shares`)])
      .then(([d, s]) => { setDir(d); setUsers(s.users.map((u) => u.id)); setSectors(s.sectors.map((x) => x.id)); })
      .catch((e) => setError((e as Error).message));
  }, [doc.id]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      await api(`/api/client/documents/${doc.id}/shares`, { method: 'PUT', body: JSON.stringify({ users, sectors }) });
      onSaved({ users: users.length, sectors: sectors.length });
      toast('Compartilhamento salvo');
      onClose();
    } catch (e) { toast((e as Error).message, 'err'); setSaving(false); }
  };
  const others = (dir?.users ?? []);
  return (
    <div className="doc-share-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="doc-share" role="dialog" aria-modal="true" aria-label={`Compartilhar ${doc.name}`} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
        <div className="sec-h"><h2>Compartilhar</h2><button type="button" className="btn sm" onClick={onClose} aria-label="Fechar"><X /></button></div>
        <p className="doc-share-name" title={doc.name}>{doc.name}</p>
        {error ? (
          <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={load}>Tentar de novo</button></div>
        ) : !dir ? (
          <div className="doc-skel" aria-busy="true"><div className="skel" /><div className="skel" /></div>
        ) : others.length <= 1 && dir.sectors.length === 0 ? (
          <div className="empty"><b>Ninguém para compartilhar</b><p>Convide pessoas ou crie setores em Empresa.</p></div>
        ) : (
          <>
            <div className="doc-share-cols">
              <fieldset><legend>Setores</legend>
                {dir.sectors.length === 0 ? <p className="mute">Nenhum setor.</p> : dir.sectors.map((s) => (
                  <label key={s.id} className="co-check"><input type="checkbox" checked={sectors.includes(s.id)} onChange={() => setSectors((l) => flip(l, s.id))} /><span>{s.name}</span></label>
                ))}
              </fieldset>
              <fieldset><legend>Pessoas</legend>
                {others.filter((u) => u.id !== doc.owner?.id).map((u) => (
                  <label key={u.id} className="co-check"><input type="checkbox" checked={users.includes(u.id)} onChange={() => setUsers((l) => flip(l, u.id))} /><span>{u.name ?? u.email}<em className="mute"> · {u.email}</em></span></label>
                ))}
              </fieldset>
            </div>
            <p className="doc-note">Quem recebe pode usar o documento nas conversas (os trechos usados aparecem nas respostas dele), mas não pode apagar nem compartilhar.</p>
            <div className="doc-share-foot"><button type="button" className="btn" onClick={onClose}>Cancelar</button><button type="button" className="btn primary" onClick={() => void save()} disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</button></div>
          </>
        )}
      </div>
    </div>
  );
}

export function Documents() {
  const { me } = useMeRequired();
  const [sharing, setSharing] = useState<Doc | null>(null);
  const [preview, setPreview] = useState<ViewerTarget | null>(null);
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);   // PDF: o servidor leva alguns segundos para extrair o texto
  const [confirming, setConfirming] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const busy = useRef(false);

  const refresh = useCallback(() => api<Doc[]>('/api/client/documents').then((l) => { setDocs(l); setError(null); }), []);
  const load = useCallback(() => { setError(null); setDocs(null); refresh().catch((e) => setError((e as Error).message)); }, [refresh]);
  useEffect(() => { load(); }, [load]);

  // envio sequencial, um arquivo por vez (o servidor barra o que não presta e a lista mostra só o que entrou)
  const send = useCallback(async (list: File[]) => {
    if (busy.current || !list.length) return;
    busy.current = true; setUploading(true);
    let sent = 0, failed = 0;
    for (const f of list) {
      const fail = (m: string) => { failed++; toast(list.length > 1 ? `${f.name}: ${m}` : m, 'err'); };
      const img = isImageName(f.name), pdf = /\.pdf$/i.test(f.name) || img;   // PDF e imagem seguem em base64 (o servidor guarda o original)
      if (pdf ? f.size > MAX_PDF_BYTES : f.size > MAX_BYTES) { fail(img ? IMG_TOO_BIG : pdf ? PDF_TOO_BIG : TOO_BIG); continue; }
      try {
        let content: { text: string } | { dataBase64: string };
        try { content = pdf ? { dataBase64: await toBase64(f) } : { text: await f.text() }; } catch { fail('não foi possível ler o arquivo'); continue; }   // ex.: uma pasta arrastada; a mensagem do navegador vem em inglês
        setExtracting(pdf && !img);
        const d = await api<Doc>('/api/client/documents', { method: 'POST', body: JSON.stringify({ name: f.name, mime: f.type, ...content }) });
        setDocs((cur) => (cur ? [d, ...cur] : cur));
        sent++;
      } catch (e) {
        fail((e as Error).message);
        if (e instanceof ApiError && e.status === 409) break;   // limite de documentos: o resto do lote cairia no mesmo erro
      }
    }
    busy.current = false; setUploading(false); setExtracting(false);
    if (sent && !failed) toast(sent === 1 ? 'Documento enviado' : `${sent} documentos enviados`);
    refresh().catch(() => {});   // reconcilia com o servidor (ordem, e a lista que ainda não tinha carregado)
  }, [refresh]);

  // arrastar e soltar arquivos em qualquer lugar da página (só reage a arquivos, não a texto ou links arrastados)
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth++; setDrag(true); };
    const over = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const leave = (e: DragEvent) => { if (!hasFiles(e)) return; depth = Math.max(0, depth - 1); if (!depth) setDrag(false); };
    const drop = (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth = 0; setDrag(false); void send(Array.from(e.dataTransfer?.files ?? [])); };
    document.addEventListener('dragenter', enter); document.addEventListener('dragover', over);
    document.addEventListener('dragleave', leave); document.addEventListener('drop', drop);
    return () => {
      document.removeEventListener('dragenter', enter); document.removeEventListener('dragover', over);
      document.removeEventListener('dragleave', leave); document.removeEventListener('drop', drop);
    };
  }, [send]);

  // enquanto algum documento está indexando (vetores em segundo plano), atualiza a lista de tempos em tempos
  const indexing = (docs ?? []).some((d) => d.indexState === 'indexing');
  useEffect(() => {
    if (!indexing) return;
    const t = setInterval(() => { refresh().catch(() => {}); }, 2500);
    return () => clearInterval(t);
  }, [indexing, refresh]);

  const reindex = async (d: Doc) => {
    try {
      const r = await api<Doc>(`/api/client/documents/${d.id}/reindex`, { method: 'POST' });
      setDocs((cur) => (cur ?? []).map((x) => (x.id === d.id ? { ...x, indexState: r.indexState } : x)));
    } catch (e) { toast((e as Error).message, 'err'); }
  };

  const open = (d: Doc) => setPreview({ documentId: d.id, name: d.name, cited: [], focus: null });

  const del = async (d: Doc) => {
    setConfirming(null);
    let gone = false;   // 404: já não existia (apagado em outra aba), some da lista com um aviso só
    try { await api(`/api/client/documents/${d.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); return; } gone = true; }
    setDocs((cur) => (cur ?? []).filter((x) => x.id !== d.id));
    toast(gone ? 'Esse documento já tinha sido apagado' : 'Documento apagado');
  };

  const uploadBtn = (
    <button type="button" className="btn primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
      <Upload />{extracting ? 'extraindo texto…' : uploading ? 'enviando…' : 'Enviar documentos'}
    </button>
  );

  return (
    <div className={`doc-page ${drag ? 'drag' : ''}`}>
      <div className="ph">
        <div>
          <span className="eyebrow">Ferramentas IA</span>
          <h1>Documentos</h1>
          <p>Envie arquivos de texto (.txt, .md ou .csv) ou PDF com texto selecionável (sem OCR). Cada um fica guardado na sua conta, dividido em trechos, e só você enxerga. Imagens (PNG, JPG, WebP) ficam guardadas só para consulta: o assistente não as lê.</p>
          <p className="doc-note">Ao conversar com um documento no Chat, os trechos usados vão junto com a pergunta e ficam no registro de uso da conta, mesmo depois de você apagar o documento.</p>
        </div>
        <div className="actions"><Link className="btn" to="/biblioteca">Biblioteca</Link>{docs && docs.length > 0 && uploadBtn}</div>
      </div>

      <input ref={fileRef} type="file" multiple hidden aria-label="Enviar documentos" accept={ACCEPT}
        onChange={(e) => { void send(Array.from(e.target.files ?? [])); e.target.value = ''; }} />

      <section className="sec" aria-busy={docs === null && !error}>
        <div className="sec-h"><h2>Seus documentos</h2>{docs && docs.length > 0 && <span className="n">{docs.length}</span>}</div>

        {error ? (
          <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={load}>Tentar de novo</button></div>
        ) : docs === null ? (
          <div className="doc-skel" aria-hidden="true"><div className="skel" /><div className="skel" /><div className="skel" /></div>
        ) : docs.length === 0 ? (
          <div className="empty"><b>Nenhum documento ainda</b><p>Guarde contratos, manuais e anotações em texto. Cada arquivo é dividido em trechos e fica só na sua conta.</p>{uploadBtn}</div>
        ) : (
          (['owner', 'shared'] as const).map((g) => {
            const rows = docs.filter((d) => (d.access ?? 'owner') === g);
            if (!rows.length) return null;
            return (
              <div key={g} className="doc-group">
                {docs.some((d) => d.access === 'shared') && <h3 className="doc-grp">{g === 'owner' ? 'Meus' : 'Compartilhados comigo'}</h3>}
                <ul className="doc-list">
                  {rows.map((d) => (
                    <li key={d.id} className="doc-row">
                      <span className="doc-ico">{d.mime.startsWith('image/') ? <ImageIcon /> : <FileText />}</span>
                      <span className="doc-namecell">
                        <button type="button" className="doc-name doc-open" title={`Pré-visualizar ${d.name}`} onClick={() => open(d)}>{d.name}</button>
                        {d.chunkCount === 0 && <span className="doc-idx off" title="Imagem: fica guardada para consulta, não entra nas respostas do chat">só visualização</span>}
                        {d.chunkCount > 0 && d.indexState === 'indexing' && <span className="doc-idx" role="status">indexando…</span>}
                        {d.chunkCount > 0 && d.indexState === 'ready' && <span className="doc-idx ok" title="Busca por significado e por palavras">pronto</span>}
                        {d.chunkCount > 0 && (d.indexState === 'failed' || !d.indexState) && (
                          <span className="doc-idx off" title={d.indexState === 'failed' ? 'A indexação falhou: a busca usa só palavras' : 'Sem vetores: a busca usa só palavras'}>
                            só busca por palavras
                            {g === 'owner' && <button type="button" className="doc-idx-btn" onClick={() => void reindex(d)} aria-label={`Reindexar ${d.name}`}>Reindexar</button>}
                          </span>
                        )}
                      </span>
                      <span className="doc-meta mono">
                        <span className="pill">{TYPE_LABEL[d.mime] ?? 'TXT'}</span>
                        <span>{fmtSize(d.bytes)}</span>
                        <span>{d.chunkCount === 0 ? 'sem texto' : `${d.chunkCount} ${d.chunkCount === 1 ? 'trecho' : 'trechos'}`}</span>
                        <span>{g === 'shared' ? `de ${d.owner?.name ?? 'colega'}` : when(d.createdAt)}</span>
                      </span>
                      <div className="doc-acts" onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>
                        {g === 'shared' ? <button type="button" onClick={() => open(d)} aria-label={`Pré-visualizar ${d.name}`} title="Pré-visualizar"><Eye /></button> : confirming === d.id ? (
                          <>
                            <button type="button" className="danger" onClick={() => void del(d)} aria-label="Confirmar: apagar documento"><Check /></button>
                            <button type="button" onClick={() => setConfirming(null)} aria-label="Cancelar" autoFocus><X /></button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => open(d)} aria-label={`Pré-visualizar ${d.name}`} title="Pré-visualizar"><Eye /></button>
                            {me.org && <button type="button" onClick={() => setSharing(d)} aria-label={`Compartilhar ${d.name}`} title={d.sharedWith && (d.sharedWith.users || d.sharedWith.sectors) ? `Compartilhado com ${d.sharedWith.users} pessoa(s) e ${d.sharedWith.sectors} setor(es)` : 'Compartilhar'}><Share2 /></button>}
                            <button type="button" className="danger" onClick={() => setConfirming(d.id)} aria-label={`Apagar ${d.name}`} title="Apagar"><Trash2 /></button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </section>
      {preview && <DocViewer target={preview} onClose={() => setPreview(null)} />}
      {sharing && <ShareDialog doc={sharing} onClose={() => setSharing(null)} onSaved={(n) => setDocs((cur) => (cur ?? []).map((x) => (x.id === sharing.id ? { ...x, sharedWith: n } : x)))} />}
    </div>
  );
}
